/**
 * TVCPlaylistView — Club TVC Playlist Manager
 *
 * Direct allocation approach: loads fixtures from commercial base,
 * shows available TVC content in a picker, writes slots directly.
 * No auto-seed chain, no round allocation JSON.
 *
 * Replaces TVCAllocationsView.jsx
 */

import React, { useState, useCallback, useEffect } from "react";
import { THEME, FONTS } from "../constants/theme";
import { AFL_CLUBS, NRL_CLUBS } from "../config/clubBases";
import commercialAirtable from "../services/commercialAirtable";
import { fetchAllAssets } from "../airtable";
import { useAuth } from "../context/AuthContext";

// ─── Club identity ───
const CLUB_CODE = (process.env.REACT_APP_DEFAULT_CLUB || "").toUpperCase();
const ALL_CLUBS = { ...AFL_CLUBS, ...NRL_CLUBS };
const CLUB_CONFIG = ALL_CLUBS[CLUB_CODE] || {};
const CLUB_NAME = CLUB_CONFIG.name || "";

// ─── Helpers ───
const g = (val) => (Array.isArray(val) ? val[0] : val);
const str = (val) => {
  if (val == null) return "";
  if (typeof val === "string") return val;
  if (Array.isArray(val)) return String(val[0] || "");
  if (typeof val === "object") return String(val.name || val.id || "");
  return String(val);
};
const FONT = FONTS.family;

// ─── Date parser — handles d/m/yyyy, yyyy-mm-dd, ISO ───
const parseDate = (raw) => {
  if (!raw) return null;
  const s = String(raw).trim();
  // d/m/yyyy or dd/mm/yyyy (Airtable cellFormat=string in AU locale)
  const slashMatch = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (slashMatch) {
    const [, day, month, year] = slashMatch;
    return new Date(Number(year), Number(month) - 1, Number(day));
  }
  // ISO or yyyy-mm-dd
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
};

const formatDate = (raw) => {
  const d = parseDate(raw);
  if (!d) return "";
  return d.toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short" });
};

// ─── Round sort order ───
const roundSortKey = (round) => {
  if (!round) return 999;
  const r = String(round).toUpperCase().trim();
  if (r === "OR") return 0;
  if (r === "QF" || r === "EF") return 100;
  if (r === "PF") return 101;
  if (r === "SF") return 102;
  if (r === "GF") return 103;
  const match = r.match(/R?(\d+)/);
  return match ? parseInt(match[1], 10) : 50;
};

// ─── Venue badge colors ───
const venueBadge = (venue) => {
  const v = String(venue || "").toLowerCase();
  if (v.includes("mcg")) return { bg: "#dbeafe", color: "#1e40af", label: "MCG" };
  if (v.includes("marvel")) return { bg: "#f3e8ff", color: "#7c3aed", label: "MRV" };
  if (v.includes("scg")) return { bg: "#fef3c7", color: "#92400e", label: "SCG" };
  return { bg: THEME.surface2, color: THEME.text, label: venue || "—" };
};

// ============================================================
// MAIN COMPONENT
// ============================================================
export default function TVCPlaylistView({ selectedRound, onHeaderInfoChange }) {
  const { isLumen8Staff } = useAuth();
  const [fixtures, setFixtures] = useState([]);
  const [reelMap, setReelMap] = useState({});    // fixtureId → reelId
  const [slots, setSlots] = useState([]);         // all club TVC slots
  const [content, setContent] = useState([]);     // available TVC content
  const [thumbMap, setThumbMap] = useState({});    // contentId/name → thumbnail URL
  // Asset map — contentId/title/etc → full asset record. Used for
  // looking up the Venue field (MCG/Marvel) for the per-row venue tag
  // since slots in the TVC reel table don't persist the destination
  // venue directly.
  const [assetMap, setAssetMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [qaProcessing, setQaProcessing] = useState(null); // slot ID being actioned

  // Picker state
  const [pickerFixture, setPickerFixture] = useState(null); // which fixture is open
  const [pickerSearch, setPickerSearch] = useState("");
  const [pickerPlays, setPickerPlays] = useState(2);
  const [isHighlightsReel, setIsHighlightsReel] = useState(false);
  const [adding, setAdding] = useState(false);
  const [contentLoading, setContentLoading] = useState(false);

  // ─── Fetch data ───
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // ─── Parallel fetch: all 3 API calls at once ───
      const [fixturesRaw, reelsRaw, slotsRaw] = await Promise.all([
        commercialAirtable.getClubFixturesForTeam(CLUB_NAME),
        commercialAirtable.getTVCReels(),
        // Server-side filter: only CLUB source slots instead of fetching entire table
        commercialAirtable.getAllTVCReelSlots({
          filterByFormula: '{Source} = "CLUB"',
        }),
      ]);

      // 1. Parse fixtures
      const parsed = fixturesRaw.map((r) => {
        const f = r.fields;
        return {
          id: r.id,
          round: str(f["Round"]) || str(f["Round Name"]) || "",
          venue: str(f["Venue"]) || str(f["Venue Name"]) || "",
          homeTeam: str(f["Home Team"]) || "",
          awayTeam: str(f["Away Team"]) || "",
          date: str(f["Date"]) || str(f["Match Date"]) || "",
          competition: str(f["Competition"]) || "",
          matchId: str(f["Match ID"]) || str(f["Name"]) || "",
        };
      }).sort((a, b) => roundSortKey(a.round) - roundSortKey(b.round));
      setFixtures(parsed);

      // Push round list to header
      const availableRounds = [...new Set(parsed.map(f => f.round).filter(Boolean))]
        .sort((a, b) => roundSortKey(a) - roundSortKey(b));
      if (onHeaderInfoChange) {
        onHeaderInfoChange((prev) => ({
          ...prev,
          rounds: availableRounds,
          selectedRound: prev.selectedRound || availableRounds[0] || "ALL",
        }));
      }

      // 2. Map fixture → reel from parallel-fetched reels
      const rMap = {};
      reelsRaw.forEach((r) => {
        const fixtureId = g(r.fields["Fixture"]) || "";
        // cellFormat=string returns fixture name, but we have record IDs from fixtures
        // Match by seeing if the fixture field value matches any fixture record
        if (fixtureId) {
          // Try direct ID match first
          if (parsed.find(f => f.id === fixtureId)) {
            rMap[fixtureId] = r.id;
          } else {
            // cellFormat=string means fixtureId is now a name like "26-AFL-CARL-01"
            // Match by matchId
            const match = parsed.find(f => f.matchId === fixtureId);
            if (match) rMap[match.id] = r.id;
          }
        }
      });
      setReelMap(rMap);

      // 3. Parse TVC reel slots — already filtered server-side to Source=CLUB
      const parsedSlots = slotsRaw
        .map((r) => {
          const f = r.fields;
          return {
            id: r.id,
            reelId: str(f["Reel"]) || str(f["TVC Reel"]) || "",
            position: f["Position"] || 0,
            clientName: str(f["Client Name"]) || str(f["Client"]) || "",
            contentId: str(f["Content ID"]) || str(f["Lumen8 ID"]) || "",
            assetName: str(f["Asset Name"]) || str(f["Name"]) || "",
            duration: str(f["Duration"]) || "30",
            source: str(f["Source"]) || "",
            status: str(f["Content Status"]) || "",
            isHighlightsReel: f["Highlights Reel"] || false,
          };
        })
        .filter((s) => s.source === "CLUB")
        .sort((a, b) => (a.position || 0) - (b.position || 0));
      setSlots(parsedSlots);

      console.log(`[TVCPlaylistView] ${parsed.length} fixtures, ${Object.keys(rMap).length} reels, ${parsedSlots.length} club slots`);
    } catch (err) {
      console.error("[TVCPlaylistView] Fetch error:", err);
      setError(err.message || "Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [onHeaderInfoChange]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ─── QA Actions (Lumen8 staff only) ───
  const handleQAAction = useCallback(async (item, newStatus) => {
    if (qaProcessing) return;
    if (newStatus === "Rejected" && !window.confirm(`Reject "${item.assetName}"? The club will need to delete and re-allocate.`)) return;
    if (newStatus === "Pending QA" && !window.confirm(`Unlock "${item.assetName}"?\n\nThis will revert to Pending QA and allow the club to modify.`)) return;
    setQaProcessing(item.ids[0]);
    try {
      await commercialAirtable.updateTVCReelSlotStatus(item.ids, newStatus);
      setSlots((prev) =>
        prev.map((s) => item.ids.includes(s.id) ? { ...s, status: newStatus } : s)
      );
    } catch (err) {
      console.error("[TVC QA] Action failed:", err);
      alert("Failed to update status. Please try again.");
    } finally {
      setQaProcessing(null);
    }
  }, [qaProcessing]);

  // ─── Load content when picker opens ───
  const loadContent = useCallback(async () => {
    if (content.length > 0) return; // already loaded
    setContentLoading(true);
    try {
      // Use same source as IngestFlow gallery — club's own Airtable base
      const assetsMap = await fetchAllAssets();
      const allAssets = Array.from(assetsMap.values());

      // Filter for TVC content uploaded by this club only
      // Exclude commercial/third-party content that may be in the shared base
      const tvcAssets = allAssets
        .filter((a) => !a.archived)
        .filter((a) => {
          // Only show content uploaded by this club (uploaderType = "Club")
          // Exclude commercial content that happens to have "TVC" in the name
          if (a.uploaderType && a.uploaderType.toLowerCase() !== "club") return false;
          const dest = (a.destination || "").toLowerCase();
          const title = (a.title || "").toUpperCase();
          return dest.includes("tvc") || title.includes("TVC") || title.includes("BS-VIDEO");
        })
        .map((a) => ({
          id: a.id,
          name: a.title || "",
          contentId: a.lumenId || "",
          client: CLUB_NAME,
          // Venue comes from the Master Content Database "Venue" field
          // via airtable.js pickVenue. Values: MCG / Marvel / SCG /
          // empty. Used by the picker modal + playlist rows to tag the
          // asset with a venue chip.
          venue: a.venue || "",
          duration: a.duration || "30",
          destination: a.destination || "",
          thumbnail: a.thumb || "",
        }));

      setContent(tvcAssets);

      // Build thumbnail + asset lookup maps. assetMap lets the playlist
      // row look up the asset's Venue by contentId or asset name, since
      // the TVC reel slot records don't persist the venue directly.
      const tMap = {};
      const aMap = {};
      allAssets.forEach((a) => {
        if (a.thumb) {
          if (a.lumenId) tMap[a.lumenId] = a.thumb;
          if (a.title) tMap[a.title] = a.thumb;
        }
        if (a.lumenId) aMap[a.lumenId] = a;
        if (a.title) aMap[a.title] = a;
        if (a.id) aMap[a.id] = a;
      });
      setThumbMap(tMap);
      setAssetMap(aMap);

      console.log(`[TVCPlaylistView] ${tvcAssets.length} TVC content items from club base (${allAssets.length} total)`);
    } catch (err) {
      console.error("[TVCPlaylistView] Content fetch error:", err);
    } finally {
      setContentLoading(false);
    }
  }, [content.length]);

  // ─── Load content library eagerly (for thumbnails + picker) ───
  useEffect(() => { loadContent(); }, [loadContent]);

  // ─── Open picker for a fixture ───
  const openPicker = useCallback((fixture) => {
    setPickerFixture(fixture);
    setPickerSearch("");
    setPickerPlays(2);
    loadContent();
  }, [loadContent]);

  // ─── Add content to fixture ───
  const handleAddContent = useCallback(async (contentItem) => {
    if (!pickerFixture || adding) return;
    setAdding(true);

    try {
      // Ensure reel exists for this fixture
      let reelId = reelMap[pickerFixture.id];
      if (!reelId) {
        reelId = await commercialAirtable.ensureTVCReel(pickerFixture.id);
        setReelMap((prev) => ({ ...prev, [pickerFixture.id]: reelId }));
      }

      // Get existing slots for position calculation
      const existingForReel = slots.filter((s) => s.reelId === reelId);

      // Create slots
      const created = await commercialAirtable.addTVCReelSlots(
        reelId,
        {
          name: contentItem.name,
          contentId: contentItem.contentId,
          client: contentItem.client,
          duration: contentItem.duration,
          isHighlightsReel: isHighlightsReel,
        },
        pickerPlays,
        existingForReel
      );

      // Add to local state
      const newSlots = created.map((r) => ({
        id: r.id,
        reelId,
        position: r.fields?.Position || 0,
        clientName: contentItem.client,
        contentId: contentItem.contentId,
        assetName: contentItem.name,
        duration: contentItem.duration,
        source: "CLUB",
        status: "Pending QA",
        isHighlightsReel: isHighlightsReel,
      }));
      setSlots((prev) => [...prev, ...newSlots]);
      setPickerFixture(null);
      setIsHighlightsReel(false); // Reset for next add

      console.log(`[TVCPlaylistView] Added ${pickerPlays} slots to reel ${reelId}`);
    } catch (err) {
      console.error("[TVCPlaylistView] Add error:", err);
      alert(`Failed to add content: ${err.message}`);
    } finally {
      setAdding(false);
    }
  }, [pickerFixture, adding, reelMap, slots, pickerPlays, isHighlightsReel]);

  // ─── Get slots for a fixture ───
  const getSlotsForFixture = (fixture) => {
    const reelId = reelMap[fixture.id];
    if (!reelId) return [];
    return slots.filter((s) => s.reelId === reelId);
  };

  // ─── Filtered content for picker ───
  const filteredContent = pickerSearch
    ? content.filter((c) =>
        c.name.toLowerCase().includes(pickerSearch.toLowerCase()) ||
        c.contentId.toLowerCase().includes(pickerSearch.toLowerCase())
      )
    : content;

  // ─── Filtered fixtures ───
  const visibleFixtures = (!selectedRound || selectedRound === "ALL")
    ? fixtures
    : fixtures.filter((f) => f.round === selectedRound);

  // Slot count for visible fixtures
  const visibleSlotCount = visibleFixtures.reduce((sum, f) => {
    return sum + getSlotsForFixture(f).length;
  }, 0);

  // Push info text to header
  useEffect(() => {
    if (!onHeaderInfoChange) return;
    const info = visibleSlotCount > 0
      ? `${visibleSlotCount} club slot${visibleSlotCount !== 1 ? "s" : ""} across ${visibleFixtures.length} fixture${visibleFixtures.length !== 1 ? "s" : ""}`
      : `${visibleFixtures.length} fixture${visibleFixtures.length !== 1 ? "s" : ""} — no content allocated`;
    onHeaderInfoChange((prev) => ({ ...prev, infoText: info }));
  }, [visibleSlotCount, visibleFixtures.length, onHeaderInfoChange]);

  // ─── Render ───
  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: "center", color: THEME.textMuted, fontFamily: FONT }}>
        <div style={{ fontSize: FONTS.base }}>Loading TVC Playlist…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 40, textAlign: "center" }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>⚠️</div>
        <div style={{ color: THEME.textSecondary, marginBottom: 12 }}>{error}</div>
        <button onClick={fetchData} style={S.retryBtn}>Retry</button>
      </div>
    );
  }

  return (
    <div style={{ padding: "0 0 48px" }}>
      {visibleFixtures.length === 0 && (
        <div style={{ padding: 40, textAlign: "center", color: THEME.textMuted }}>
          {fixtures.length === 0
            ? `No fixtures found for ${CLUB_NAME || "this club"}.`
            : `No fixtures for ${selectedRound}.`}
        </div>
      )}

      {/* Fixture list */}
      {visibleFixtures.map((fixture) => {
        const fixtureSlots = getSlotsForFixture(fixture);
        const vb = venueBadge(fixture.venue);
        const dateStr = formatDate(fixture.date);

        // Group slots by content name
        const grouped = {};
        fixtureSlots.forEach((s) => {
          const key = s.assetName || s.contentId || s.id;
          if (!grouped[key]) grouped[key] = { ...s, count: 0, ids: [] };
          grouped[key].count++;
          grouped[key].ids.push(s.id);
          // Track status — use first slot's status (all slots for same content should match)
          if (!grouped[key].status && s.status) grouped[key].status = s.status;
        });
        const groupedList = Object.values(grouped);

        return (
          <div key={fixture.id} style={S.fixtureCard}>
            {/* Fixture header */}
            <div style={S.fixtureHeader}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1 }}>
                <span style={S.roundBadge}>{fixture.round || "—"}</span>
                <span style={{ fontSize: FONTS.base, fontWeight: FONTS.semibold, color: THEME.textOnDark }}>
                  {fixture.homeTeam}
                </span>
                <span style={{ fontSize: FONTS.sm, color: "rgba(255,255,255,0.4)" }}>v</span>
                <span style={{ fontSize: FONTS.base, fontWeight: FONTS.semibold, color: THEME.textOnDark }}>
                  {fixture.awayTeam}
                </span>
                {dateStr && (
                  <span style={{ fontSize: FONTS.xs, color: "rgba(255,255,255,0.45)", marginLeft: 4 }}>
                    {dateStr}
                  </span>
                )}
                <span style={{
                  fontSize: FONTS.xs, fontWeight: FONTS.bold, padding: "2px 8px",
                  borderRadius: 4, background: vb.bg, color: vb.color,
                }}>
                  {vb.label}
                </span>
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {fixtureSlots.length > 0 && (
                  <span style={S.slotCount}>
                    {fixtureSlots.length} spot{fixtureSlots.length !== 1 ? "s" : ""}
                  </span>
                )}
                <button onClick={() => openPicker(fixture)} style={S.addBtn}>
                  + Add Content
                </button>
              </div>
            </div>

            {/* Content rows */}
            {groupedList.length === 0 ? (
              <div style={S.emptyRow}>
                <span style={{ color: THEME.textMuted, fontSize: FONTS.sm }}>
                  No content allocated — click "+ Add Content" to get started
                </span>
              </div>
            ) : (
              <div>
                {/* Table header */}
                <div style={{ ...S.tableHeader, gap: 8 }}>
                  <span style={{ width: 40, flexShrink: 0 }}></span>
                  <span style={{ flex: 1, minWidth: 0 }}>CONTENT</span>
                  <span style={{ width: 120, flexShrink: 0 }}>CONTENT ID</span>
                  <span style={{ width: 60, flexShrink: 0, textAlign: "center" }}>PLAYS</span>
                  <span style={{ width: 60, flexShrink: 0, textAlign: "center" }}>DUR.</span>
                  <span style={{ width: isLumen8Staff ? 240 : 36, flexShrink: 0, textAlign: "center", fontSize: 10, color: THEME.textMuted }}>
                    {isLumen8Staff ? "QA ACTIONS" : ""}
                  </span>
                </div>
                {groupedList.map((item) => {
                  const thumb = thumbMap[item.contentId] || thumbMap[item.assetName] || null;
                  const isApproved = (item.status || "").toLowerCase() === "approved";
                  const isRejected = (item.status || "").toLowerCase() === "rejected";
                  const isPending = !isApproved && !isRejected;
                  const rowBg = isApproved
                    ? "rgba(34,197,94,0.06)"
                    : isRejected
                    ? "rgba(239,68,68,0.06)"
                    : "transparent";
                  const borderColor = isApproved
                    ? "rgba(34,197,94,0.15)"
                    : isRejected
                    ? "rgba(239,68,68,0.15)"
                    : THEME.lineLight;
                  return (
                  <div
                    key={item.ids[0]}
                    style={{ ...S.contentRow, background: rowBg, borderBottomColor: borderColor, cursor: isApproved ? "default" : undefined }}
                    onClick={isApproved ? () => alert("This content has been approved and locked by Lumen8.\n\nIf you need to edit your content, contact your Lumen8 coordinator.") : undefined}
                  >
                    <div style={S.rowThumb}>
                      {thumb ? (
                        <img src={thumb} alt="" style={S.thumbImg} />
                      ) : (
                        <div style={S.thumbPlaceholder}>🎬</div>
                      )}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{
                          fontSize: FONTS.base, fontWeight: FONTS.medium, color: THEME.text,
                          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                        }}>
                          {item.assetName}
                        </span>
                        {/* Venue tag — shows MCG / Marvel / SCG for the
                            asset itself, sourced from Master Content
                            Database "Venue" field. Looked up via
                            assetMap since the TVC reel schema doesn't
                            persist destination on the slot record. Silent
                            when venue unknown (better than a misleading
                            default). */}
                        {(() => {
                          const asset = assetMap[item.contentId] || assetMap[item.assetName] || null;
                          const assetVenue = asset?.venue || "";
                          if (!assetVenue) return null;
                          const vb = venueBadge(assetVenue);
                          return (
                            <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 6px", borderRadius: 4, background: vb.bg, color: vb.color, letterSpacing: "0.02em" }}>
                              {vb.label}
                            </span>
                          );
                        })()}
                        {isApproved && (
                          <span style={S.statusApproved}>✓ Approved</span>
                        )}
                        {isRejected && (
                          <span style={S.statusRejected}>✗ Rejected</span>
                        )}
                        {!isApproved && !isRejected && item.status && (
                          <span style={S.statusPending}>Pending QA</span>
                        )}
                        {item.isHighlightsReel && (
                          <span style={{ fontSize: 9, fontWeight: 700, padding: "2px 6px", borderRadius: 4, background: "rgba(249,115,22,0.15)", color: "#f97316", border: "1px solid rgba(249,115,22,0.3)" }}>
                            HIGHLIGHTS
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: FONTS.xs, color: THEME.textMuted, marginTop: 1 }}>
                        {item.clientName || "Club"}
                      </div>
                    </div>
                    <span style={{ width: 120, flexShrink: 0, fontSize: FONTS.xs, color: THEME.textSecondary, fontFamily: "monospace" }}>
                      {item.contentId}
                    </span>
                    <span style={{ width: 60, flexShrink: 0, textAlign: "center" }}>
                      <span style={S.playsBadge}>×{item.count}</span>
                    </span>
                    <span style={{ width: 60, flexShrink: 0, textAlign: "center", fontSize: FONTS.xs, color: THEME.textSecondary }}>
                      {item.duration}s
                    </span>
                    {/* ─── Actions column ─── */}
                    <div style={{ width: isLumen8Staff ? 240 : 36, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                      {isLumen8Staff ? (
                        /* Lumen8 staff: inline QA actions (matching Commercial QA) */
                        <>
                          {isPending && (
                            <>
                              <button onClick={(e) => { e.stopPropagation(); handleQAAction(item, "Approved"); }}
                                disabled={qaProcessing === item.ids[0]}
                                style={{ ...S.qaApproveBtn, opacity: qaProcessing === item.ids[0] ? 0.5 : 1 }}>
                                ✓ Approve
                              </button>
                              <button onClick={(e) => { e.stopPropagation(); handleQAAction(item, "Rejected"); }}
                                disabled={qaProcessing === item.ids[0]}
                                style={{ ...S.qaRejectBtn, opacity: qaProcessing === item.ids[0] ? 0.5 : 1 }}>
                                ✗ Reject
                              </button>
                            </>
                          )}
                          {isApproved && (
                            <button onClick={(e) => { e.stopPropagation(); handleQAAction(item, "Pending QA"); }}
                              disabled={qaProcessing === item.ids[0]}
                              style={{ ...S.qaUnlockBtn, opacity: qaProcessing === item.ids[0] ? 0.5 : 1 }}>
                              🔓 Unlock
                            </button>
                          )}
                          {isRejected && (
                            <button onClick={(e) => { e.stopPropagation(); handleQAAction(item, "Approved"); }}
                              disabled={qaProcessing === item.ids[0]}
                              style={{ ...S.qaApproveBtn, opacity: qaProcessing === item.ids[0] ? 0.5 : 1 }}>
                              ↩ Re-approve
                            </button>
                          )}
                          {/* Delete — always available for Lumen8 */}
                          <button onClick={(e) => {
                              e.stopPropagation();
                              if (window.confirm(`Remove all ${item.count} slot(s) for "${item.assetName}"?`)) {
                                item.ids.forEach((id) => { commercialAirtable.deleteTVCReelSlot(id).catch(() => {}); });
                                setSlots((prev) => prev.filter((s) => !item.ids.includes(s.id)));
                              }
                            }}
                            style={S.deleteBtn}
                            title="Delete">
                            🗑️
                          </button>
                        </>
                      ) : (
                        /* Club users: existing behavior */
                        isApproved ? (
                          <span style={{ fontSize: 14 }}>🔒</span>
                        ) : (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (window.confirm(`Remove all ${item.count} slot(s) for "${item.assetName}"?`)) {
                                item.ids.forEach((id) => { commercialAirtable.deleteTVCReelSlot(id).catch(() => {}); });
                                setSlots((prev) => prev.filter((s) => !item.ids.includes(s.id)));
                              }
                            }}
                            style={S.deleteBtn}
                            title="Remove"
                          >
                            🗑️
                          </button>
                        )
                      )}
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      {/* ─── Asset Picker Modal ─── */}
      {pickerFixture && (
        <div style={S.overlay} onClick={() => !adding && setPickerFixture(null)}>
          <div style={S.modal} onClick={(e) => e.stopPropagation()}>
            {/* Modal header */}
            <div style={S.modalHeader}>
              <div>
                <div style={{ fontSize: FONTS.md, fontWeight: FONTS.bold, color: THEME.textOnDark }}>
                  Add TVC Content
                </div>
                <div style={{ fontSize: FONTS.sm, color: "rgba(255,255,255,0.5)", marginTop: 2 }}>
                  {pickerFixture.round} · {pickerFixture.homeTeam} v {pickerFixture.awayTeam}
                </div>
              </div>
              <button onClick={() => setPickerFixture(null)} style={S.closeBtn}>✕</button>
            </div>

            {/* Search + plays */}
            <div style={S.modalControls}>
              <input
                type="text"
                placeholder="Search content…"
                value={pickerSearch}
                onChange={(e) => setPickerSearch(e.target.value)}
                style={S.searchInput}
                autoFocus
              />
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ fontSize: FONTS.sm, color: THEME.textSecondary }}>Plays:</span>
                {[1, 2, 3, 4].map((n) => (
                  <button
                    key={n}
                    onClick={() => setPickerPlays(n)}
                    style={{
                      ...S.playsOption,
                      background: pickerPlays === n ? THEME.primary : THEME.surface2,
                      color: pickerPlays === n ? "#fff" : THEME.text,
                      fontWeight: pickerPlays === n ? 700 : 400,
                    }}
                  >
                    ×{n}
                  </button>
                ))}
              </div>
              <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", marginLeft: "auto" }}>
                <input
                  type="checkbox"
                  checked={isHighlightsReel}
                  onChange={(e) => setIsHighlightsReel(e.target.checked)}
                  style={{ cursor: "pointer", accentColor: "#f97316" }}
                />
                <span style={{ fontSize: FONTS.sm, fontWeight: FONTS.semibold, color: isHighlightsReel ? "#f97316" : THEME.textSecondary }}>
                  Highlights Reel
                </span>
              </label>
            </div>

            {/* Content list */}
            <div style={S.modalList}>
              {contentLoading ? (
                <div style={{ padding: 32, textAlign: "center", color: THEME.textMuted }}>
                  Loading content…
                </div>
              ) : filteredContent.length === 0 ? (
                <div style={{ padding: 32, textAlign: "center", color: THEME.textMuted }}>
                  {pickerSearch ? "No matching content found" : "No TVC content available"}
                </div>
              ) : (
                filteredContent.map((item) => (
                  <div
                    key={item.id}
                    style={S.pickerRow}
                    onClick={() => !adding && handleAddContent(item)}
                  >
                    <div style={S.pickerThumb}>
                      {item.thumbnail ? (
                        <img src={item.thumbnail} alt="" style={S.thumbImg} />
                      ) : (
                        <div style={S.thumbPlaceholder}>🎬</div>
                      )}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        display: "flex", alignItems: "center", gap: 8,
                        overflow: "hidden",
                      }}>
                        <span style={{
                          fontSize: FONTS.base, fontWeight: FONTS.medium, color: THEME.text,
                          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                        }}>
                          {item.name}
                        </span>
                        {/* Venue tag — Master Content Database "Venue"
                            value, matches the tag shown in the playlist
                            row after add so users can predict what they
                            get. */}
                        {item.venue && (() => {
                          const vb = venueBadge(item.venue);
                          return (
                            <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 6px", borderRadius: 4, background: vb.bg, color: vb.color, letterSpacing: "0.02em", flexShrink: 0 }}>
                              {vb.label}
                            </span>
                          );
                        })()}
                      </div>
                      <div style={{ fontSize: FONTS.xs, color: THEME.textMuted, marginTop: 2 }}>
                        {item.contentId}
                        {item.destination && ` · ${item.destination}`}
                      </div>
                    </div>
                    <span style={{
                      fontSize: FONTS.xs, fontWeight: FONTS.semibold, color: THEME.primary,
                      whiteSpace: "nowrap", padding: "4px 10px",
                      borderRadius: 6, background: THEME.primaryLight,
                    }}>
                      + Add ×{pickerPlays}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// STYLES
// ============================================================
const S = {
  fixtureCard: {
    margin: "12px 16px", borderRadius: THEME.radius,
    border: `1px solid ${THEME.line}`, background: THEME.surface,
    overflow: "hidden", boxShadow: THEME.shadow,
    fontFamily: FONT,
  },
  fixtureHeader: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "10px 16px", borderBottom: `1px solid ${THEME.line}`,
    background: THEME.dark, color: THEME.textOnDark,
  },
  roundBadge: {
    fontSize: FONTS.xs, fontWeight: FONTS.extrabold, color: THEME.primary,
    background: "rgba(47,214,181,0.15)", padding: "3px 10px",
    borderRadius: THEME.radiusSm, fontFamily: "monospace", letterSpacing: 0.5,
  },
  slotCount: {
    fontSize: FONTS.xs, fontWeight: FONTS.bold, color: THEME.primary,
    background: "rgba(47,214,181,0.12)", padding: "3px 10px",
    borderRadius: THEME.radiusFull,
  },
  addBtn: {
    fontSize: FONTS.sm, fontWeight: FONTS.semibold, color: "#111",
    background: "linear-gradient(135deg, #2fd6b5 0%, #84cc16 100%)",
    border: "none",
    borderRadius: THEME.radiusSm, padding: "6px 16px", cursor: "pointer",
    transition: THEME.transitionFast, fontFamily: FONT,
    boxShadow: "0 1px 4px rgba(47,214,181,0.3)",
  },
  emptyRow: {
    padding: "20px 16px", textAlign: "center",
  },
  tableHeader: {
    display: "flex", alignItems: "center", padding: "6px 16px",
    fontSize: FONTS.xs, fontWeight: FONTS.bold, color: THEME.textMuted,
    letterSpacing: 0.8, borderBottom: `1px solid ${THEME.lineLight}`,
    background: THEME.tableHeader, fontFamily: FONT,
  },
  contentRow: {
    display: "flex", alignItems: "center", padding: "10px 16px",
    borderBottom: `1px solid ${THEME.lineLight}`, gap: 8,
    transition: THEME.transitionFast, fontFamily: FONT,
  },
  playsBadge: {
    fontSize: FONTS.xs, fontWeight: FONTS.bold, color: THEME.primary,
    background: THEME.primaryLight, padding: "2px 8px",
    borderRadius: 4, fontFamily: "monospace",
  },
  deleteBtn: {
    background: "none", border: "none", cursor: "pointer",
    fontSize: 14, padding: "2px 4px", opacity: 0.4,
    transition: THEME.transitionFast,
  },
  qaApproveBtn: {
    fontSize: 12, fontWeight: 700, color: "#fff", background: "#16a34a",
    border: "none", borderRadius: 6, padding: "5px 14px",
    cursor: "pointer", fontFamily: "'Space Grotesk', sans-serif",
    boxShadow: "0 1px 4px rgba(22,163,74,0.3)", whiteSpace: "nowrap",
  },
  qaRejectBtn: {
    fontSize: 12, fontWeight: 700, color: "#fff", background: "#dc2626",
    border: "none", borderRadius: 6, padding: "5px 14px",
    cursor: "pointer", fontFamily: "'Space Grotesk', sans-serif",
    boxShadow: "0 1px 4px rgba(220,38,38,0.3)", whiteSpace: "nowrap",
  },
  qaUnlockBtn: {
    fontSize: 12, fontWeight: 700, color: "#e2e8f0", background: "rgba(255,255,255,0.08)",
    border: "1px solid rgba(255,255,255,0.15)", borderRadius: 6, padding: "5px 14px",
    cursor: "pointer", fontFamily: "'Space Grotesk', sans-serif", whiteSpace: "nowrap",
  },
  statusApproved: {
    fontSize: FONTS.xs, fontWeight: FONTS.bold, color: "#16a34a",
    background: "rgba(34,197,94,0.12)", padding: "2px 8px",
    borderRadius: 4, whiteSpace: "nowrap",
  },
  statusRejected: {
    fontSize: FONTS.xs, fontWeight: FONTS.bold, color: "#dc2626",
    background: "rgba(239,68,68,0.12)", padding: "2px 8px",
    borderRadius: 4, whiteSpace: "nowrap",
  },
  statusPending: {
    fontSize: FONTS.xs, fontWeight: FONTS.medium, color: "#d97706",
    background: "rgba(217,119,6,0.1)", padding: "2px 8px",
    borderRadius: 4, whiteSpace: "nowrap",
  },
  retryBtn: {
    fontSize: FONTS.base, fontWeight: FONTS.semibold, color: THEME.textOnPrimary,
    background: THEME.primary, border: "none",
    borderRadius: THEME.radiusSm, padding: "8px 24px", cursor: "pointer",
    fontFamily: FONT,
  },

  // Modal
  overlay: {
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
    display: "flex", alignItems: "center", justifyContent: "center",
    zIndex: 9999,
  },
  modal: {
    width: 620, maxHeight: "85vh", background: THEME.surface,
    borderRadius: THEME.radiusLg, boxShadow: THEME.shadowXl, display: "flex",
    flexDirection: "column", overflow: "hidden", fontFamily: FONT,
  },
  modalHeader: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "16px 20px", borderBottom: `1px solid ${THEME.line}`,
    background: THEME.dark, color: THEME.textOnDark,
  },
  closeBtn: {
    background: "none", border: "none", fontSize: 18,
    color: "rgba(255,255,255,0.5)", cursor: "pointer", padding: "4px 8px",
  },
  modalControls: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "12px 20px", gap: 12, borderBottom: `1px solid ${THEME.lineLight}`,
    background: THEME.tableHeader,
  },
  searchInput: {
    flex: 1, padding: "8px 12px", borderRadius: THEME.radiusSm,
    border: `1px solid ${THEME.line}`, fontSize: FONTS.base,
    outline: "none", background: THEME.surface,
    fontFamily: FONT,
  },
  playsOption: {
    width: 36, height: 30, borderRadius: THEME.radiusSm,
    border: "none", cursor: "pointer", fontSize: FONTS.sm,
    fontFamily: "monospace", transition: THEME.transitionFast,
  },
  modalList: {
    flex: 1, overflowY: "auto", maxHeight: "55vh",
    scrollbarWidth: "thin",
  },
  pickerRow: {
    display: "flex", alignItems: "center", padding: "10px 20px",
    borderBottom: `1px solid ${THEME.lineLight}`, cursor: "pointer",
    gap: 12, transition: THEME.transitionFast,
  },
  pickerThumb: {
    width: 48, height: 36, borderRadius: 4, overflow: "hidden",
    background: THEME.surface2, flexShrink: 0,
    display: "flex", alignItems: "center", justifyContent: "center",
  },
  rowThumb: {
    width: 40, height: 30, borderRadius: 4, overflow: "hidden",
    background: THEME.surface2, flexShrink: 0,
    display: "flex", alignItems: "center", justifyContent: "center",
  },
  thumbImg: {
    width: "100%", height: "100%", objectFit: "cover",
  },
  thumbPlaceholder: {
    fontSize: 16, opacity: 0.4,
  },
};
